/**
 * PATH: apps/quote/src/pages/pricelist-page/ItemAttachmentsPanel.tsx
 * PURPOSE: Attach spec sheets / datasheets to a pricelist item and show the
 *          auto-generated QA checklist. Edit-mode only (needs a saved item id).
 * WHY: Phase 5 — "spec-sheet upload -> QA checklist -> attach to PriceBookItem."
 * FLOW: list attachments → upload (multipart) → server generates QA checklist
 *       → render checklist; delete / regenerate.
 * RELATED: pricing-api (listItemAttachments/uploadItemAttachment/…), ItemEditorModal
 */
import { useRef, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload, Trash2, RefreshCw, Loader2, FileText, CheckSquare, Sparkles } from 'lucide-react';
import { quoteApi } from '@/lib/quoteApi';

interface ItemAttachmentsPanelProps {
  pricebookId: string;
  itemId: string;
}

const KINDS = ['spec', 'datasheet', 'image', 'cert', 'other'];

export default function ItemAttachmentsPanel({ pricebookId, itemId }: ItemAttachmentsPanelProps) {
  const qc = useQueryClient();
  const fileRef = useRef<HTMLInputElement>(null);
  const [kind, setKind] = useState('spec');
  const key = ['item-attachments', pricebookId, itemId];

  const { data: attachments = [], isLoading } = useQuery({
    queryKey: key,
    queryFn: () => quoteApi.listItemAttachments(pricebookId, itemId),
  });

  const uploadMutation = useMutation({
    mutationFn: (file: File) => quoteApi.uploadItemAttachment(pricebookId, itemId, file, kind),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const deleteMutation = useMutation({
    mutationFn: (id: string) => quoteApi.deleteItemAttachment(pricebookId, itemId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const regenMutation = useMutation({
    mutationFn: (id: string) => quoteApi.regenerateItemChecklist(pricebookId, itemId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });
  const aiMutation = useMutation({
    mutationFn: (id: string) => quoteApi.aiEnrichAttachment(pricebookId, itemId, id),
    onSuccess: () => qc.invalidateQueries({ queryKey: key }),
  });

  const onPick = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadMutation.mutate(file);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="border-t border-gray-100 dark:border-gray-700 px-5 py-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-gray-700 dark:text-gray-200 flex items-center gap-2">
          <FileText className="h-4 w-4" /> Spec sheets &amp; QA
        </h3>
        <div className="flex items-center gap-2">
          <select value={kind} onChange={(e) => setKind(e.target.value)} className="text-xs border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-900">
            {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
          </select>
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploadMutation.isPending}
            className="flex items-center gap-1 text-xs px-3 py-1.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {uploadMutation.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
            Upload
          </button>
          <input ref={fileRef} type="file" hidden onChange={onPick} accept=".pdf,.xlsx,.xls,.png,.jpg,.jpeg,.doc,.docx" />
        </div>
      </div>

      {isLoading ? (
        <div className="py-4 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-gray-400" /></div>
      ) : !attachments.length ? (
        <p className="text-xs text-gray-500">No spec sheets yet. Upload one to auto-generate a QA checklist.</p>
      ) : (
        <div className="space-y-2">
          {attachments.map((a: any) => (
            <div key={a.id} className="rounded-lg border border-gray-100 dark:border-gray-700 p-3">
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-700">{a.kind}</span>
                {a.file_url
                  ? <a href={a.file_url} target="_blank" rel="noreferrer" className="text-sm text-blue-600 hover:underline truncate">{a.filename || 'file'}</a>
                  : <span className="text-sm text-gray-900 dark:text-white truncate">{a.filename || 'file'}</span>}
                <div className="ml-auto flex items-center gap-1">
                  <button
                    onClick={() => aiMutation.mutate(a.id)}
                    disabled={aiMutation.isPending && aiMutation.variables === a.id}
                    title="AI-enrich QA checklist + suggestions from this spec"
                    className="p-1.5 hover:bg-violet-50 rounded disabled:opacity-50"
                  >
                    {aiMutation.isPending && aiMutation.variables === a.id
                      ? <Loader2 className="h-3.5 w-3.5 text-violet-500 animate-spin" />
                      : <Sparkles className="h-3.5 w-3.5 text-violet-500" />}
                  </button>
                  <button onClick={() => regenMutation.mutate(a.id)} title="Regenerate checklist" className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
                    <RefreshCw className="h-3.5 w-3.5 text-gray-500" />
                  </button>
                  <button onClick={() => deleteMutation.mutate(a.id)} title="Delete" className="p-1.5 hover:bg-red-50 rounded">
                    <Trash2 className="h-3.5 w-3.5 text-red-500" />
                  </button>
                </div>
              </div>
              {a.ai_summary && (
                <p className="mt-1.5 text-xs italic text-violet-700 dark:text-violet-300">{a.ai_summary}</p>
              )}
              {Array.isArray(a.qa_checklist) && a.qa_checklist.length > 0 && (
                <ul className="mt-2 space-y-1">
                  {a.qa_checklist.map((c: any) => (
                    <li key={c.id} className="flex items-start gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <CheckSquare className="h-3.5 w-3.5 mt-0.5 text-gray-400 flex-shrink-0" />
                      <span>{c.text}<span className="ml-1 text-gray-400">({c.category})</span></span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
